import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  useCreateTeam,
  useUpdateTeam,
  type TeamWithMembers,
} from "@/hooks/useTeams";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { useToast } from "@/hooks/useToast";
import { TEAM_CATEGORIES } from "@/utils/constants";
import type { TeamCategory } from "@/types/database";

interface TeamFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (teamId: string) => void;
  team?: TeamWithMembers;
}

const initialFormData = {
  name: "",
  category: "Beatitude Family 1" as TeamCategory,
  description: "",
  max_capacity: "",
  leader_id: "",
};

export function TeamForm({ open, onClose, onSuccess, team }: TeamFormProps) {
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const { toast } = useToast();
  const { data: teamMembers = [] } = useTeamMembers(team?.id);

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (open) {
      setFormData(
        team
          ? {
              name: team.name,
              category: team.category,
              description: team.description || "",
              max_capacity: team.max_capacity?.toString() || "",
              leader_id: team.leader_id || "",
            }
          : initialFormData,
      );
    }
  }, [open, team]);

  const isEditing = !!team;
  const isPending = createTeam.isPending || updateTeam.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        description: formData.description || null,
        max_capacity: formData.max_capacity
          ? parseInt(formData.max_capacity)
          : null,
        leader_id: formData.leader_id || null,
      };

      if (isEditing) {
        await updateTeam.mutateAsync({ id: team.id, updates: payload });
        toast({ title: "Équipe mise à jour" });
        onClose();
        onSuccess?.(team.id);
      } else {
        const newTeam = await createTeam.mutateAsync(payload);
        toast({ title: "Équipe créée" });
        setFormData(initialFormData);
        onClose();
        onSuccess?.(newTeam.id);
      }
    } catch {
      toast({
        title: "Erreur",
        description: isEditing
          ? "Impossible de mettre à jour l'équipe"
          : "Impossible de créer l'équipe",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier l'équipe" : "Nouvelle équipe"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Nom *</Label>
            <Input
              id="team-name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label>Catégorie *</Label>
                <HelpTooltip content="Beatitude Family 1-3 : équipes de la communauté des Béatitudes. Professional Group : musiciens professionnels externes. Mixed : équipe mixte communauté/professionnels." />
              </div>
              <Select
                value={formData.category}
                onValueChange={(v) =>
                  setFormData({ ...formData, category: v as TeamCategory })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="team-max_capacity">Capacité max</Label>
                <HelpTooltip content="Nombre maximum de membres dans cette équipe. Utile pour la planification des événements." />
              </div>
              <Input
                id="team-max_capacity"
                type="number"
                value={formData.max_capacity}
                onChange={(e) =>
                  setFormData({ ...formData, max_capacity: e.target.value })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-description">Description</Label>
            <Textarea
              id="team-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>

          {isEditing && teamMembers.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label>Chef d'équipe</Label>
                <HelpTooltip content="Sélectionnez le chef de cette équipe parmi ses membres actuels." />
              </div>
              <Select
                value={formData.leader_id}
                onValueChange={(v) =>
                  setFormData({ ...formData, leader_id: v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un chef (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.user.full_name || member.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEditing
                  ? "Mise à jour..."
                  : "Création..."
                : isEditing
                  ? "Mettre à jour"
                  : "Créer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
